
import {
    Box, TableCell
} from "@mui/material";
import { Grid, MenuItem, TextField } from "@material-ui/core";
import { store } from 'app';
import React, { useEffect } from "react";
import AppSnackbar from 'components/snackbar/snackbar';
import SimpleBackdrop from 'common/components/loaders/simple-backdrop-loader';
import { AutomationConstant, AutomationType } from '../api/automation-constants';
import AutomationAPI, { AutomationEmailReadyToSendRequest, AutomationEmailReadyToSendResponse, AutomationTaskReadyToSendRequest, AutomationTextLogRequest, AutomationTextReadyToSendResponse, GetReadyToSendTaskDataResponse } from '../api/automation-api';
import { BaseResponse } from 'common/utils/shared/shared-models';
import ReusableTable from './reusable-table';
import DOMPurify from 'dompurify';
import axios, { CancelTokenSource } from "axios";

const commonTextFieldProps = {
    fullWidth: true,
    variant: "outlined" as const,
};
const automationTypeOptions = Object.keys(AutomationType)
    .filter((key) => isNaN(Number(key)))
    .map((key) => ((
        <MenuItem key={AutomationType[key as keyof typeof AutomationType]} value={AutomationType[key as keyof typeof AutomationType]}>
            {key}
        </MenuItem>
    )));


const AutomationQueue: React.FC = () => {
    const [showSnackbar] = AppSnackbar.useSnackBar();
    const [logsBackdropLoading, setLogsBackdropLoading] = React.useState(false);

    // #region Automation Logs State

    const [autoType, setAutoType] = React.useState<number>(AutomationType.Email);

    const [data, setData] = React.useState<{

        smsReadyToSend: AutomationTextReadyToSendResponse[] | null;
        emailsReadyToSend: AutomationEmailReadyToSendResponse[] | null;
        tasksReadyToSend: GetReadyToSendTaskDataResponse[] | null;
    }>({

        smsReadyToSend: null,
        emailsReadyToSend: null,
        tasksReadyToSend: null
    });

    // #endregion Automation Logs State

    // #region Automation Logs

    useEffect(() => {
        const getQueueToken = axios.CancelToken.source();
        callAutomationQueue(getQueueToken);
        return () => {
            getQueueToken.cancel('Request canceled due to component unmount or parameter change.');
        };
    }, [autoType])

    const callAutomationQueue = async (getQueueToken:CancelTokenSource): Promise<void> => {
        switch (autoType) {
            case AutomationType.Email:
                await GetEmailReadyToSend(getQueueToken);
                break;
            case AutomationType.Text:
                await GetTextReadyToSend(getQueueToken);
                break;
            case AutomationType.Task:
                await GetTasksReadyToSend(getQueueToken);
                break;
            default:
                break;
        }
    }

    const handleError = (error: any, fallbackMessage: string): void => {
        const errorMessage = error?.errorDetails?.[0] || error?.message || error?.error || fallbackMessage;
        showSnackbar({ type: 'error', message: errorMessage });
    };

    async function GetEmailReadyToSend(getQueueToken:CancelTokenSource): Promise<void> {
        setLogsBackdropLoading(true);
        try {
            let textReq: AutomationEmailReadyToSendRequest = {
                dmsDealerId: (store.getState().user as { dmsDealerId: number }).dmsDealerId,
            }

            const response: BaseResponse<Array<AutomationEmailReadyToSendResponse>> = await AutomationAPI.GetReadyToSendEmailData(textReq,getQueueToken.token);
            if (!response.isSuccess) {
                handleError(response, AutomationConstant.unableToFetchLogs);
                return;
            }

            setData(prev => ({
                ...prev,
                emailsReadyToSend: response.data,
                smsReadyToSend: null,
                tasksReadyToSend: null
            }));

        } catch (error: unknown) {
            if (axios.isCancel(error)) return;
            handleError(error, AutomationConstant.unableToFetchLogs);
        } finally {
            setLogsBackdropLoading(false);
        }
    }

    async function GetTextReadyToSend(getQueueToken:CancelTokenSource): Promise<void> {
        setLogsBackdropLoading(true);
        try {
            let textReq: AutomationTextLogRequest = {
                dmsDealerId: (store.getState().user as { dmsDealerId: number }).dmsDealerId,
            }

            const response: BaseResponse<Array<AutomationTextReadyToSendResponse>> = await AutomationAPI.GetReadyToSendTextData(textReq,getQueueToken.token);
            if (!response.isSuccess) {
                handleError(response, AutomationConstant.unableToFetchLogs);
                return;
            }

            setData(prev => ({
                ...prev,
                emailsReadyToSend: null,
                smsReadyToSend: response.data,
                tasksReadyToSend: null
            }));

        } catch (error: unknown) {
            if (axios.isCancel(error)) return;
            handleError(error, AutomationConstant.unableToFetchLogs);
        } finally {
            setLogsBackdropLoading(false);
        }
    }

    async function GetTasksReadyToSend(getQueueToken:CancelTokenSource): Promise<void> {
        setLogsBackdropLoading(true);
        try {
            let taskReq: AutomationTaskReadyToSendRequest = {
                dmsDealerId: (store.getState().user as { dmsDealerId: number }).dmsDealerId,
            }


            const response: BaseResponse<Array<GetReadyToSendTaskDataResponse>> = await AutomationAPI.GetReadyToSendTaskData(taskReq,getQueueToken.token);
            if (!response.isSuccess) {
                handleError(response, AutomationConstant.unableToFetchLogs);
                return;
            }

            setData(prev => ({
                ...prev,
                emailsReadyToSend: null,
                smsReadyToSend: null,
                tasksReadyToSend: response.data
            }));

        } catch (error: unknown) {
            if (axios.isCancel(error)) return;
            handleError(error, AutomationConstant.unableToFetchLogs);
        } finally {
            setLogsBackdropLoading(false);
        }
    }

    // #endregion Automation Logs

    return (
        <Box style={{ marginTop: "10px" }}>

            <Grid container spacing={2} alignItems="center" justify="flex-end" style={{ marginTop: 16, marginBottom: 8 }}>
                <Grid item xs={12}>
                    <Box display="flex" justifyContent="flex-end">
                        <TextField
                            select
                            {...commonTextFieldProps}
                            name="autoType"
                            label="Automation Type"
                            size="small"
                            value={autoType}
                            onChange={(e) => setAutoType(Number(e.target.value))}
                            style={{ width: 180 }}
                        >
                            {automationTypeOptions}
                        </TextField>
                    </Box>
                </Grid>
            </Grid>


            <ReusableTable
                columns={['Location', 'Invoice', 'Job#', 'Status', 'Email', 'Subject', 'Message']}
                data={data.emailsReadyToSend}
                renderRow={(row) => (
                    <>
                        <TableCell valign='top'>{row.dealerName}</TableCell>
                        <TableCell valign='top'>{row.invoiceId}</TableCell>
                        <TableCell valign='top'>{row.jobId}</TableCell>
                        <TableCell valign='top'>{row.jobStatus}</TableCell>
                        <TableCell valign='top' >{row.emailAddress}</TableCell>
                        <TableCell valign='top'>{row.emailSubject}</TableCell>
                        <TableCell valign='top'>{<div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(row.emailMessage) }} />}</TableCell>
                    </>
                )}
            />

            <ReusableTable
                columns={['Location', 'Invoice', 'Job#', 'Status', 'Department#', 'Customer#', 'Message']}
                data={data.smsReadyToSend}
                renderRow={(row) => (
                    <>
                        <TableCell valign='top'>{row.dealerId}</TableCell>
                        <TableCell valign='top'>{row.invoiceId}</TableCell>
                        <TableCell valign='top'>{row.jobId}</TableCell>
                        <TableCell valign='top'>{row.jobStatus}</TableCell>
                        <TableCell valign='top'>{row.phoneNumberDepartment}</TableCell>
                        <TableCell valign='top'>{row.phoneNumberCustomer}</TableCell>
                        <TableCell valign='top'>{row.textMessage}</TableCell>
                    </>
                )}
            />

            <ReusableTable
                columns={['Location', 'Invoice', 'Job#', 'Status', 'Task Subject', 'Assigned User']}
                data={data.tasksReadyToSend}
                renderRow={(row: GetReadyToSendTaskDataResponse) => (
                    <>
                        <TableCell valign="top">{row.dealerId}</TableCell>
                        <TableCell valign="top">{row.invoiceId}</TableCell>
                        <TableCell valign="top">{row.jobId}</TableCell>
                        <TableCell valign="top">{row.jobStatus}</TableCell>
                        <TableCell valign="top">{row.taskSubject}</TableCell>
                        <TableCell valign="top">{row.assignedUser}</TableCell>
                    </>
                )}
            />

            {logsBackdropLoading && (<SimpleBackdrop />)}
        </Box>
    );
}


export default AutomationQueue;